import {
  useGetResourceAgendaPublicBoard,
  useGetResourceAgendaPublicTeamSchedule,
  useGetResourceAgendaTeamSchedule,
  usePublicViewEvents,
  useViewEvents,
  type GetResourceAgendaTeamScheduleResponse
} from "@swivu/client-api";
import { DateTime } from "luxon";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback
} from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import groupTeamSchedule, {
  GridData,
  GroupByStrategiesKeys
} from "./groupTeamSchedule.js";
import { groupBy } from "@swivu/shared-ui";

const views = [
  { value: "tile", text: "Tile" },
  { value: "row", text: "Row" }
] as const;

const infoDensities = [
  { text: "Multi-line", value: " line-clamp-4 " },
  { text: "Two lines", value: " line-clamp-2 " },
  { text: "One line", value: " line-clamp-1 " }
] as const;

type FilteredData = {
  id: number;
  board: number;
  tasks: {
    id: number;
    display_name: string;
    color: string;
    start_time: string;
    end_time: string;
    style?: string | null;
  }[];
  display_name?: string | null;
  label?: string | null;
}[];

interface TeamScheduleContextProps {
  teamSchedule: GetResourceAgendaTeamScheduleResponse;
  search: string;
  dateRange: Date[];
  changeDays: (addOrSubtractDays: "+" | "-") => void;
  resetDays: () => void;
  isLoading: boolean;
  gridData: GridData | null;
  groupedByTaskData: GridData | null;
  groupBy: GroupByStrategiesKeys;
  handleGroupBy: (groupBy: GroupByStrategiesKeys) => void;
  boardName: string | null;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  view: (typeof views)[number];
  views: typeof views;
  setView: React.Dispatch<React.SetStateAction<(typeof views)[number]>>;
  infoDensity: (typeof infoDensities)[number];
  infoDensities: typeof infoDensities;
  setInfoDensity: React.Dispatch<
    React.SetStateAction<(typeof infoDensities)[number]>
  >;
  isPublic: boolean | undefined;
  slideoutContainerId?: string;
  timezone: string;
  viewId?: number | null;
}

const TeamScheduleContext = createContext<TeamScheduleContextProps | undefined>(
  undefined
);

export type TeamScheduleV2Props =
  // team schedule
  | {
      isPublic: false;
      viewId: null;
      dateRange: { start_datetime: string; end_datetime: string };
      enabled: true;
      board_ids: number[];
      slug: undefined | null;
    }
  // public team schedule
  | {
      isPublic: true;
      viewId: null;
      dateRange: { start_datetime: string; end_datetime: string };
      enabled: true;
      board_ids: null | undefined;
      slug: string;
    }
  // shared view
  | {
      isPublic: false;
      viewId: number;
      dateRange: { start_datetime: string; end_datetime: string };
      enabled: true;
      board_ids: undefined | null;
      slug: null | undefined;
    } // shared public view
  | {
      isPublic: true;
      viewId: number;
      dateRange: { start_datetime: string; end_datetime: string };
      enabled: true;
      board_ids: undefined | null;
      slug: string;
    };

// Transform view events data to common format
type ViewEventItem = {
  resource_id: number;
  resource_label?: string | null;
  task_id: number;
  task_name?: string | null;
  task_color?: string | null;
  task_style?: string | null;
  start_time: string;
  end_time: string;
};

const transformViewEventsData = (data: ViewEventItem[] | undefined) => {
  const groupByResource = groupBy(data ?? [], "resource_id");
  return Object.entries(groupByResource).map(([k, v]) => ({
    tasks: v.map((r) => ({
      id: r.task_id,
      display_name: r.task_name ?? "",
      start_time: r.start_time,
      end_time: r.end_time,
      color: r.task_color ?? null,
      style: r.task_style
    })),
    display_name: v[0].resource_label ?? null,
    label: v[0].resource_label ?? null,
    id: Number(k),
    board: "test"
  }));
};

// Determine which API variant to use
type ApiVariant =
  | "publicViewEvents"
  | "publicTeamSchedule"
  | "teamSchedule"
  | "viewEvents";

const getApiVariant = (props: TeamScheduleV2Props): ApiVariant => {
  if (props.isPublic) {
    return props.viewId ? "publicViewEvents" : "publicTeamSchedule";
  }
  if ("board_ids" in props && Array.isArray(props.board_ids)) {
    return "teamSchedule";
  }
  return "viewEvents";
};

// All hooks called unconditionally, enabled flag controls which one runs
const useApi = (props: TeamScheduleV2Props) => {
  const { start_datetime, end_datetime } = props.dateRange;
  const variant = getApiVariant(props);

  const publicViewEventsResult = usePublicViewEvents({
    variables: {
      id: String(props.viewId ?? ""),
      start_datetime,
      end_datetime,
      slug: props.isPublic ? props.slug : ""
    },
    enabled: variant === "publicViewEvents"
  });

  const publicTeamScheduleResult = useGetResourceAgendaPublicTeamSchedule({
    variables: {
      slug: props.isPublic ? props.slug : "",
      start_datetime,
      end_datetime
    },
    enabled: variant === "publicTeamSchedule"
  });

  const teamScheduleResult = useGetResourceAgendaTeamSchedule({
    variables: {
      start_datetime,
      end_datetime,
      board_ids:
        "board_ids" in props && Array.isArray(props.board_ids)
          ? props.board_ids
          : []
    },
    enabled: variant === "teamSchedule"
  });

  const viewEventsResult = useViewEvents({
    variables: {
      id: String(props.viewId ?? ""),
      start_datetime,
      end_datetime
    },
    enabled: variant === "viewEvents"
  });

  // Return the active result based on variant
  if (variant === "publicViewEvents") {
    return {
      ...publicViewEventsResult,
      data: transformViewEventsData(publicViewEventsResult.data)
    };
  }
  if (variant === "publicTeamSchedule") {
    return publicTeamScheduleResult;
  }
  if (variant === "teamSchedule") {
    return teamScheduleResult;
  }
  return {
    ...viewEventsResult,
    data: transformViewEventsData(viewEventsResult.data)
  };
};

// left this hook in the likely event we'll need to change or transform the data
const useTeamScheduleData = (props: TeamScheduleV2Props) => {
  const { data, isLoading, status: _status } = useApi(props);
  return { data, isLoading };
};

const DAYS_IN_CALENDAR = 5;

const initDateRange = ({ timeZone }: { timeZone: string }) => {
  const today = DateTime.now().setZone(timeZone).startOf("day");

  const plus5Days = today
    .plus({ days: DAYS_IN_CALENDAR })
    .endOf("day")
    .minus({ second: 1 });

  const start_datetime = today.minus({ days: 2 }).toISO()!;
  const end_datetime = plus5Days.minus({ days: 2 }).toISO()!;
  return { start_datetime, end_datetime };
};

const initialQueryDates = ({ timeZone }: { timeZone: string }) => {
  let { start_datetime, end_datetime } = initDateRange({ timeZone });

  const params = new URLSearchParams();

  if (params.get("start_datetime") && params.get("end_datetime")) {
    start_datetime = decodeURIComponent(params.get("start_datetime")!);
    end_datetime = decodeURIComponent(params.get("end_datetime")!);
    return { start_datetime, end_datetime };
  }

  const localDateRange = JSON.parse(
    sessionStorage.getItem("teamScheduleDateRange") ?? "{}"
  );

  if (localDateRange.start_datetime && localDateRange.end_datetime) {
    start_datetime = localDateRange.start_datetime;
    end_datetime = localDateRange.end_datetime;
    return { start_datetime, end_datetime };
  }

  return { start_datetime, end_datetime };
};

const useDateRange = ({ timeZone }: { timeZone: string }) => {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [numberOfDaysShown, _setNumberOfDaysShown] = useState(DAYS_IN_CALENDAR);

  // Initialize date range from URL or session storage
  const [dateRange, setDateRange] = useState(() => ({
    start_datetime: initialQueryDates({ timeZone }).start_datetime,
    end_datetime: initialQueryDates({ timeZone }).end_datetime
  }));

  // Single effect to handle all URL and storage syncing
  useEffect(() => {
    const start = params.get("start_datetime");
    const end = params.get("end_datetime");
    const currentStart = start ? decodeURIComponent(start) : "";
    const currentEnd = end ? decodeURIComponent(end) : "";

    // Save current state to session storage
    sessionStorage.setItem("teamScheduleDateRange", JSON.stringify(dateRange));

    // Only update URL if values are different and not empty
    if (dateRange.start_datetime && dateRange.end_datetime) {
      if (
        currentStart !== dateRange.start_datetime ||
        currentEnd !== dateRange.end_datetime
      ) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("start_datetime", dateRange.start_datetime);
        searchParams.set("end_datetime", dateRange.end_datetime);
        setParams(searchParams, { replace: true });
      }
    }
  }, [dateRange]);

  const resetDays = () => {
    const { start_datetime, end_datetime } = initDateRange({
      timeZone
    });

    setDateRange({
      start_datetime,
      end_datetime
    });
  };

  const changeDays = useCallback(
    (addOrSubtractDays: "+" | "-") => {
      // alert("changeDays");
      const daysToAdjust =
        numberOfDaysShown * (addOrSubtractDays === "+" ? 1 : -1);

      const startDate = DateTime.fromISO(dateRange.start_datetime, {
        zone: timeZone
      }).plus({ days: daysToAdjust });

      const endDate = startDate.plus({ days: numberOfDaysShown }).endOf("day");
      // .minus({ second: 1 });

      setDateRange({
        start_datetime: startDate.toISO()!,
        end_datetime: endDate.toISO()!
      });
    },
    [dateRange, numberOfDaysShown, timeZone]
  );

  const formattedDateRange = useMemo(() => {
    const startDate = DateTime.fromISO(dateRange.start_datetime);
    return Array.from({ length: DAYS_IN_CALENDAR }, (_, i) =>
      startDate
        .plus({ days: i })
        // .minus({ second: i === DAYS_IN_CALENDAR - 1 ? 1 : 0 })
        .toJSDate()
    );
  }, [dateRange.start_datetime]);

  return {
    resetDays,
    dateRange: formattedDateRange,
    changeDays,
    dateRangeBounds: dateRange
  };
};

type TeamScheduleProviderProps = (
  | {
      children: ReactNode;
      viewId: number;
    }
  | {
      children: ReactNode;
      isPublic: boolean;
      viewId?: number | null;
    }
  | {
      children: ReactNode;
      board_ids: number[];
    }
) & { slideoutContainerId?: string; timezone: string };

const sanitizeVariables = ({
  props,
  slug,
  dateRangeBounds
}: {
  props: TeamScheduleProviderProps; // Still needed to check for viewId
  slug: string | undefined | null;
  dateRangeBounds: { start_datetime: string; end_datetime: string };
}): TeamScheduleV2Props => {
  const board_ids = "board_ids" in props ? props.board_ids : undefined;
  const viewId = "viewId" in props ? props.viewId : undefined;
  const enabled = true;

  if ("isPublic" in props && props.isPublic) {
    if (!slug) {
      throw new Error("Slug is required");
    }
    if (viewId) {
      // Case 4: Shared Public View
      return {
        isPublic: true,
        viewId,
        dateRange: dateRangeBounds,
        enabled: true,
        board_ids: null,
        slug: slug
      };
    }
    // Case 2: Public Team Schedule
    return {
      isPublic: true,
      viewId: null,
      dateRange: dateRangeBounds,
      enabled,
      board_ids: null, // Assuming API for public team schedule doesn't need board_ids
      slug: slug
    };
  }

  if (viewId) {
    // Case 3: Shared View (Private)
    return {
      isPublic: false,
      viewId,
      dateRange: dateRangeBounds,
      enabled: true,
      board_ids: null,
      slug: null
    };
  }

  // Not public cases
  if (board_ids) {
    // Case 1: Standard Team Schedule
    return {
      isPublic: false,
      viewId: null,
      dateRange: dateRangeBounds,
      enabled,
      board_ids: board_ids,
      slug: undefined
    };
  }

  throw new Error("Invalid props to TeamScheduleProvider");
};

const TeamScheduleProvider = (props: TeamScheduleProviderProps) => {
  const { slug } = useParams();
  const { children } = props;
  const isPublic = "isPublic" in props ? props.isPublic : undefined;

  const [search, setSearch] = useState(
    localStorage.getItem("teamScheduleTextSearch") ?? ""
  );
  const savedView = localStorage.getItem("teamScheduleView");
  const initialView = savedView ? JSON.parse(savedView) : views[0];

  const savedInfoDensity = localStorage.getItem("teamScheduleInfoDensity");
  const initialInfoDensity = savedInfoDensity
    ? JSON.parse(savedInfoDensity)
    : infoDensities[0];

  const savedGroupBy = localStorage.getItem("teamScheduleGroupBy");
  // if viewId is in props, lock groupBy to task -
  const initialGroupBy =
    "viewId" in props
      ? "resource"
      : savedGroupBy
        ? JSON.parse(savedGroupBy)
        : "task";

  const [view, setView] = useState<(typeof views)[number]>(
    initialView as (typeof views)[number]
  );

  const [infoDensity, setInfoDensity] =
    useState<(typeof infoDensities)[number]>(initialInfoDensity);

  const [groupBy, setGroupBy] = useState<GroupByStrategiesKeys>(initialGroupBy);
  const { dateRange, changeDays, dateRangeBounds, resetDays } = useDateRange({
    timeZone: props.timezone
  });
  const { data: board } = useGetResourceAgendaPublicBoard({
    variables: {
      slug: slug ?? ""
    },

    enabled: !!slug && isPublic
  });

  useEffect(() => {
    localStorage.setItem("teamScheduleGroupBy", JSON.stringify(groupBy));
  }, [groupBy]);
  useEffect(() => {
    // set to local storage
    localStorage.setItem("teamScheduleTextSearch", search);
  }, [search]);

  useEffect(() => {
    // set to local storage
    localStorage.setItem(
      "teamScheduleInfoDensity",
      JSON.stringify(infoDensity)
    );
  }, [infoDensity]);

  useEffect(() => {
    // set to local storage
    localStorage.setItem("teamScheduleView", JSON.stringify(view));
  }, [view]);

  const variables = sanitizeVariables({
    props,
    slug,
    dateRangeBounds
  });

  const { data, isLoading } = useTeamScheduleData(variables);
  const filteredData = useMemo(
    () =>
      data?.filter((task) => {
        if (!search) return true;
        // filter if search is not empty and task name is in search
        if (
          task?.display_name?.toLowerCase().includes(search.toLowerCase()) ||
          task?.label?.toLowerCase().includes(search.toLowerCase()) ||
          task?.tasks.some((t) =>
            t.display_name?.toLowerCase().includes(search.toLowerCase())
          )
        ) {
          return true;
        }
        return false;
      }),
    [data, search]
  );
  const handleGroupBy = (groupBy: GroupByStrategiesKeys) => {
    sessionStorage.setItem("teamScheduleGroupBy", JSON.stringify(groupBy));
    setGroupBy(groupBy);
  };

  const gridData = groupTeamSchedule(groupBy, dateRange, props.timezone, {
    isPublic,
    slug
  })((filteredData ?? []) as FilteredData);

  const filteredGridData = gridData;
  const groupedByTaskData = groupTeamSchedule(
    "task",
    dateRange,
    props.timezone,
    { isPublic, slug }
  )((filteredData ?? []) as FilteredData);

  return (
    <TeamScheduleContext.Provider
      value={{
        viewId: variables.viewId,
        search,
        boardName: board?.board_name ?? null,
        groupBy,
        handleGroupBy,
        gridData: filteredGridData,
        groupedByTaskData,
        teamSchedule: (filteredData ?? []) as FilteredData,
        dateRange,
        changeDays,
        resetDays,
        isLoading,
        setSearch,
        view,
        views,
        setView,
        infoDensity,
        infoDensities,
        setInfoDensity,
        isPublic,
        slideoutContainerId: props.slideoutContainerId,
        timezone: props.timezone
      }}
    >
      {children}
    </TeamScheduleContext.Provider>
  );
};

const useTeamSchedule = () => {
  const context = useContext(TeamScheduleContext);
  if (context === undefined) {
    throw new Error(
      "useTeamSchedule must be used within a TeamScheduleProvider"
    );
  }
  return context;
};

export { TeamScheduleProvider, useTeamSchedule };
